import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { organizationService } from '../../application/OrganizationService';
import * as routing from '../../constants/Routing';
import { BillingPeriod, PlanType } from '../../domain/subscription/Subscription';
import useErrorMiddleware from '../errorMiddleware/useErrorMiddleware';
import { useShowSnackbarMessage } from '../snackbarMessage/useShowSnackbarMessage';

interface UseCreateOrganizationPaymentLinkProps {
  plan: PlanType;
  billingPeriod: BillingPeriod;
}

type UseCreateOrganizationPaymentLinkResponseProps = ServiceResponse<
  ({ plan, billingPeriod }: UseCreateOrganizationPaymentLinkProps) => void,
  string
>;

// Hooks act as 'Adapter' layer.
export const useCreateOrganizationPaymentLink = (): UseCreateOrganizationPaymentLinkResponseProps => {
  const { sessionExpiredLogout } = useErrorMiddleware();
  const { showSnackbarMessage } = useShowSnackbarMessage();
  const [paymentLink, setPaymentLink] = useState<string | undefined>(undefined);

  const { isLoading, isSuccess, isError, mutate } = useMutation(
    ({ plan, billingPeriod }: UseCreateOrganizationPaymentLinkProps): Promise<string> => {
      return organizationService.createPaymentLink({
        redirectUrl: `${window.location.origin}${routing.SETTINGS.BILLING.SUBSCRIPTION}`,
        plan,
        billingPeriod,
      });
    },
    {
      onSuccess: (organizationPaymentLink) => {
        setPaymentLink(organizationPaymentLink);
      },
      onError: (err: any) => {
        console.info(`Create Organization Payment Link Error: `, err?.response);

        if (err?.response?.status === 401) {
          sessionExpiredLogout();
          return;
        }
        showSnackbarMessage({
          messageIntlId: `infoerror.${err?.response?.data?.messageId ?? `network_error`}`,
        });
      },
    }
  );

  return {
    error: isError,
    isLoading: isLoading,
    success: isSuccess,
    data: paymentLink,
    run: ({ plan, billingPeriod }: UseCreateOrganizationPaymentLinkProps) => mutate({ plan, billingPeriod }),
  };
};
